import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DescriptionField from './DescriptionField';
import ExtractFieldsList from './ExtractFieldsList';
import { clientsApi, agentsApi } from '../../services/api';
import './AgentForm.css';

const AgentForm = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_name: '',
    first_message: '',
    fields_to_extract: [{ name: '', type: 'text', required: true, display_order: 0 }],
    voice_gender: 'male', // Gender selection for UI
    language: 'es', // Language selection
    requires_email: false, // Email requirement checkbox
    enhance_with_ai: false, // AI enhancement checkbox - defaults to false
    enable_memory: false, // Memory system checkbox
    memory_retention_days: 365 // Memory retention period
  });
  
  // Clients data for dropdown
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Load clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await clientsApi.getClients();
        setClients(response.clients || []);
      } catch (err) {
        setError('Failed to load clients. Please try again.');
        console.error(err);
      }
    };
    
    fetchClients();
  }, []);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle field changes
  const handleFieldChange = (index, field) => {
    const updatedFields = [...formData.fields_to_extract];
    updatedFields[index] = field;
    
    setFormData(prev => ({
      ...prev,
      fields_to_extract: updatedFields
    }));
  };
  
  // Add a new field
  const addField = () => {
    const newField = {
      name: '',
      type: 'text',
      required: true,
      display_order: formData.fields_to_extract.length
    };
    
    setFormData(prev => ({
      ...prev,
      fields_to_extract: [...prev.fields_to_extract, newField]
    }));
  };
  
  // Remove a field
  const removeField = (index) => {
    const updatedFields = formData.fields_to_extract.filter((_, i) => i !== index);
    
    // Update display order after removal
    const reorderedFields = updatedFields.map((field, i) => ({
      ...field,
      display_order: i
    }));
    
    setFormData(prev => ({
      ...prev,
      fields_to_extract: reorderedFields
    }));
  };
  
  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.description || !formData.client_name || !formData.first_message) {
      setError('Please fill all required fields');
      return;
    }
    
    if (formData.fields_to_extract.length === 0) {
      setError('Please add at least one field to extract');
      return;
    }
    
    // Validate all fields have names
    const invalidFields = formData.fields_to_extract.some(field => !field.name);
    if (invalidFields) {
      setError('All fields must have a name');
      return;
    }
    
    // Map gender and language to appropriate voice ID
    const voiceIdMapping = {
      // Spanish voices
      'male-es': 'w7IU2bIH6xHcyfkUUWi3', // Confirmed working Spanish male voice
      'female-es': 'CaJslL1xziwefCeTNzHv', // Spanish female voice
      
      // English voices - using ElevenLabs recommended voice IDs for English
      'male-en': 'w7IU2bIH6xHcyfkUUWi3', // Adam - English male voice
      'female-en': 'CaJslL1xziwefCeTNzHv'  // Bella - English female voice
    };
    
    // Create combined key for voice selection
    const voiceKey = `${formData.voice_gender}-${formData.language}`;
    
    // Create a copy of the form data with the voice_id mapped from combined gender-language key
    const submitData = {
      ...formData,
      voice_id: voiceIdMapping[voiceKey], // Using the combined key for accurate voice selection
      requires_email: formData.requires_email || false // Ensure requires_email is included
    };
    
    // Remove voice_gender as it's not expected by the API
    delete submitData.voice_gender;
    
    setLoading(true);
    setError('');
    
    try {
      // Send to API
      await agentsApi.createAgent(submitData);
      
      // Redirect to agents list on success
      navigate('/agents');
    } catch (err) {
      setError(`Failed to create agent: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="agent-form-container">
      <h2>Create New Agent</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name" className="form-label">Agent Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-control"
            placeholder="Enter agent name"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="client_id" className="form-label">Client Name</label>
          <input
            type="text"
            id="client_name"
            name="client_name"
            value={formData.client_name}
            onChange={handleChange}
            className="form-control"
            placeholder="Enter client name"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="first_message" className="form-label">Agent First Message</label>
          <input
            type="text"
            id="first_message"
            name="first_message"
            value={formData.first_message}
            onChange={handleChange}
            className="form-control"
            placeholder="Enter the first message that the agent will say exactly as you want it to be"
            required
          />
        </div>
        
        <DescriptionField
          value={formData.description}
          onChange={handleChange}
          enhanceWithAi={formData.enhance_with_ai}
          onEnhanceWithAiChange={(e) => {
            setFormData(prev => ({
              ...prev,
              enhance_with_ai: e.target.checked
            }));
          }}
        />
        
        {/* Webhook URL is now fixed and handled by the backend */}
        
        <div className="form-group">
          <label htmlFor="voice_gender" className="form-label">Voice Gender</label>
          <select
            id="voice_gender"
            name="voice_gender"
            value={formData.voice_gender}
            onChange={handleChange}
            className="form-control"
            required
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="language" className="form-label">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleChange}
            className="form-control"
          >
            <option value="es">Spanish (es)</option>
           {/* <option value="en">English (en)</option> */}
          </select>
        </div>
        
        {/* Email Collection Section */}
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="requires_email"
              checked={formData.requires_email || false}
              onChange={(e) => {
                setFormData(prev => ({
                  ...prev,
                  requires_email: e.target.checked
                }));
              }}
            />
            Ask for email address before conversation
          </label>
          <div className="form-help-text">
            When enabled, users will need to provide a valid email address before starting the conversation.
          </div>
        </div>
        
        {/* Memory Configuration Section */}
        <div className="form-group">
          <div className="memory-section" style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            border: '1px solid #dee2e6', 
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <label className="checkbox-label" style={{ fontWeight: 'bold', fontSize: '16px' }}>
              <input
                type="checkbox"
                name="enable_memory"
                checked={formData.enable_memory || false}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    enable_memory: e.target.checked
                  }));
                }}
                style={{ marginRight: '8px' }}
              />
              🧠 Enable Memory (Cross-Conversation Learning)
            </label>
            <div className="form-help-text" style={{ marginTop: '8px', color: '#6c757d' }}>
              When enabled, the agent will remember previous conversations with the same user across sessions.
            </div>
            
            {/* Memory Settings (shown when enabled) */}
            {formData.enable_memory && (
              <div style={{ marginTop: '15px', marginLeft: '20px' }}>
                <div className="row">
                  <div className="col-md-6">
                    <label htmlFor="memory_retention_days" className="form-label">
                      Memory Retention Period
                    </label>
                    <select
                      id="memory_retention_days"
                      name="memory_retention_days"
                      value={formData.memory_retention_days || 365}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                      <option value={365}>1 year (recommended)</option>
                      <option value={730}>2 years</option>
                      <option value={-1}>Permanent</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Memory Categories</label>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      ✓ Conversation context<br/>
                      ✓ User preferences<br/>
                      ✓ Progress tracking<br/>
                      ✓ Key insights
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <ExtractFieldsList
          fields={formData.fields_to_extract}
          onFieldChange={handleFieldChange}
          onAddField={addField}
          onRemoveField={removeField}
        />
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => navigate('/agents')}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn" 
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Agent'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgentForm;
